<template>
  <section class="page" data-testid="page-good-stuff">
    <header class="page-header">
      <div>
        <h1>好东西</h1>
        <p>收集金句、业界情报、有用链接和零散笔记。</p>
      </div>
      <div class="actions">
        <button class="button" :disabled="!items.length" @click="copyCurrentView">复制当前视图</button>
        <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中" : "刷新" }}</button>
      </div>
    </header>

    <div class="metrics-grid">
      <MetricCard label="全部" :value="items.length" />
      <MetricCard label="高分" :value="qualityMetrics.highScore" />
      <MetricCard label="带链接" :value="qualityMetrics.withUrl" />
      <MetricCard label="带来源" :value="qualityMetrics.withSource" />
      <MetricCard label="标签数" :value="qualityMetrics.tags" />
      <MetricCard label="金句" :value="countByType.quote" />
      <MetricCard label="情报" :value="countByType.intel" />
      <MetricCard label="链接" :value="countByType.link" />
      <MetricCard label="笔记" :value="countByType.note" />
    </div>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <p v-if="error" class="error-text">{{ error }}</p>

    <div class="split">
      <div class="panel">
        <h2>收藏库</h2>
        <div class="toolbar">
          <select v-model="filters.item_type" @change="() => load()">
            <option value="">全部类型</option>
            <option value="quote">金句</option>
            <option value="intel">业界情报</option>
            <option value="link">有用链接</option>
            <option value="note">笔记</option>
          </select>
          <select v-model="filters.status" @change="() => load()">
            <option value="active">活跃</option>
            <option value="archived">已归档</option>
          </select>
          <input v-model="filters.q" placeholder="搜索标题、内容、链接" @keyup.enter="() => load()" />
          <input v-model="filters.tag" placeholder="标签" @keyup.enter="() => load()" />
          <button class="button" :class="{ primary: qualityFilter === 'all' }" @click="qualityFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: qualityFilter === 'high' }" @click="qualityFilter = 'high'">高分</button>
          <button class="button" :class="{ primary: qualityFilter === 'link' }" @click="qualityFilter = 'link'">带链接</button>
          <button class="button" :class="{ primary: qualityFilter === 'source' }" @click="qualityFilter = 'source'">有来源</button>
          <button class="button" @click="() => load()">筛选</button>
          <button class="button" :disabled="loading" @click="randomPick">随机抽取</button>
        </div>
        <div v-if="randomItem" class="random-pick">
          <strong>{{ typeLabel(randomItem.item_type) }} · {{ randomItem.title }}</strong>
          <span>{{ randomItem.content }}</span>
          <div class="row-actions">
            <button class="button small" @click="edit(randomItem)">编辑</button>
            <button class="button small" @click="copyItem(randomItem)">复制</button>
          </div>
        </div>
        <DataTable :columns="columns" :rows="filteredItems" row-key="id">
          <template #item_type="{ row }">{{ typeLabel(row.item_type) }}</template>
          <template #status="{ row }"><span class="status-badge" :class="row.status === 'active' ? 'ok' : 'pending'">{{ row.status }}</span></template>
          <template #content="{ row }"><pre>{{ row.content }}</pre></template>
          <template #url="{ row }"><a v-if="row.url" :href="row.url" target="_blank" rel="noreferrer">打开</a><span v-else>-</span></template>
          <template #tags="{ row }"><span>{{ (row.tags || []).map((tag: string) => `#${tag}`).join(" ") || "-" }}</span></template>
          <template #source="{ row }">{{ sourceText(row) }}</template>
          <template #actions="{ row }">
            <div class="row-actions">
              <button class="button small" @click="selectItem(row)">详情</button>
              <button class="button small" @click="edit(row)">编辑</button>
              <button class="button small" @click="copyItem(row)">复制</button>
              <button v-if="row.status === 'archived'" class="button small" @click="restore(row)">恢复</button>
              <button v-else class="button small danger" @click="archive(row)">归档</button>
            </div>
          </template>
        </DataTable>
      </div>

      <div class="panel">
        <h2>{{ form.id ? "编辑" : "新增" }}</h2>
        <div class="form-grid">
          <FormField label="类型">
            <select v-model="form.item_type">
              <option value="quote">金句</option>
              <option value="intel">业界情报</option>
              <option value="link">有用链接</option>
              <option value="note">笔记</option>
            </select>
          </FormField>
          <FormField label="分数"><input v-model.number="form.score" type="number" /></FormField>
        </div>
        <FormField label="标题"><input v-model="form.title" /></FormField>
        <FormField label="链接"><input v-model="form.url" placeholder="https://..." /></FormField>
        <FormField label="标签，逗号分隔"><input v-model="tagInput" placeholder="AI, 投资, 工具" /></FormField>
        <FormField label="内容"><textarea v-model="form.content" /></FormField>
        <footer class="actions">
          <button class="button primary" :disabled="loading || !form.content.trim()" @click="save">{{ form.id ? "保存" : "添加" }}</button>
          <button class="button" @click="resetForm">清空</button>
        </footer>
        <div class="material-advice" :class="formQuality.level">
          <strong>{{ formQuality.title }}</strong>
          <span>{{ formQuality.detail }}</span>
        </div>
      </div>
    </div>

    <div class="split">
      <div class="panel">
        <h2>素材详情</h2>
        <div v-if="selectedItem" class="detail-panel">
          <div class="runtime-grid compact">
            <div><span>类型</span><strong>{{ typeLabel(selectedItem.item_type) }}</strong></div>
            <div><span>状态</span><strong>{{ selectedItem.status }}</strong></div>
            <div><span>分数</span><strong>{{ selectedItem.score }}</strong></div>
            <div><span>来源</span><strong>{{ sourceText(selectedItem) }}</strong></div>
          </div>
          <pre>{{ JSON.stringify(selectedItem, null, 2) }}</pre>
        </div>
        <div v-else class="empty">选择一条素材查看完整来源和 JSON。</div>
      </div>
      <div class="panel">
        <h2>素材复用建议</h2>
        <div class="diagnostic-list">
          <div v-for="item in diagnostics" :key="item.label" class="diagnostic-row" :class="item.level">
            <strong>{{ item.label }}</strong>
            <span>{{ item.detail }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson, putJson } from "../api/client";
import type { GoodStuffItem } from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择素材内容");
const items = ref<GoodStuffItem[]>([]);
const randomItem = ref<GoodStuffItem | null>(null);
const selectedItem = ref<GoodStuffItem | null>(null);
const tagInput = ref("");
const filters = reactive({ item_type: "", status: "active", q: "", tag: "" });
const qualityFilter = ref<"all" | "high" | "link" | "source">("all");
const form = reactive({
  id: 0,
  item_type: "note" as GoodStuffItem["item_type"],
  title: "",
  content: "",
  url: "",
  score: 0
});

const columns = [
  { key: "id", label: "ID" },
  { key: "item_type", label: "类型" },
  { key: "status", label: "状态" },
  { key: "title", label: "标题" },
  { key: "content", label: "内容" },
  { key: "url", label: "链接" },
  { key: "tags", label: "标签" },
  { key: "score", label: "分数" },
  { key: "source", label: "来源" },
  { key: "actions", label: "操作" }
];

const countByType = computed(() => {
  const counts = { quote: 0, intel: 0, link: 0, note: 0 };
  for (const item of items.value) counts[item.item_type] += 1;
  return counts;
});
const filteredItems = computed(() => {
  return items.value.filter((item) => {
    if (qualityFilter.value === "high" && Number(item.score || 0) < 5) return false;
    if (qualityFilter.value === "link" && !item.url) return false;
    if (qualityFilter.value === "source" && !hasSource(item)) return false;
    return true;
  });
});
const qualityMetrics = computed(() => {
  const tags = new Set<string>();
  for (const item of items.value) for (const tag of item.tags || []) tags.add(tag);
  return {
    highScore: items.value.filter((item) => Number(item.score || 0) >= 5).length,
    withUrl: items.value.filter((item) => !!item.url).length,
    withSource: items.value.filter(hasSource).length,
    tags: tags.size
  };
});
const formQuality = computed(() => {
  if (!form.content.trim()) return { level: "warning", title: "内容为空", detail: "先填入可复用的素材正文。" };
  if (form.item_type === "link" && !form.url.trim()) return { level: "warning", title: "链接缺失", detail: "链接类素材最好补 URL，方便之后打开验证。" };
  if (!tagsFromInput().length) return { level: "warning", title: "缺少标签", detail: "添加 1-3 个标签会让随机抽取和检索更好用。" };
  return { level: "ok", title: "素材可复用", detail: "内容、类型和标签已经足够进入素材池。" };
});
const diagnostics = computed(() => {
  const noTags = items.value.filter((item) => !(item.tags || []).length).length;
  const noSource = items.value.filter((item) => !hasSource(item)).length;
  const highScore = qualityMetrics.value.highScore;
  return [
    {
      label: "可复用素材",
      detail: highScore ? `${highScore} 条高分素材适合随机抽取或人格化引用` : "暂无高分素材，可以给常用素材提高分数",
      level: highScore ? "ok" : "warning"
    },
    {
      label: "标签质量",
      detail: noTags ? `${noTags} 条素材缺少标签` : "当前列表素材都有标签",
      level: noTags ? "warning" : "ok"
    },
    {
      label: "来源可追溯",
      detail: noSource ? `${noSource} 条素材缺少群聊/用户来源` : "当前列表素材都带来源",
      level: noSource ? "warning" : "ok"
    }
  ];
});

function typeLabel(type: string) {
  return { quote: "金句", intel: "业界情报", link: "有用链接", note: "笔记" }[type] || type;
}
function sourceText(row: GoodStuffItem) {
  const chat = row.source_chat_type ? `${row.source_chat_type}:${row.source_chat_id}` : "-";
  return `${row.source_nickname || row.source_user_id || "-"} / ${chat}`;
}
function hasSource(row: GoodStuffItem) {
  return !!(row.source_user_id || row.source_nickname || row.source_chat_id || row.source_chat_type);
}
function tagsFromInput() {
  return tagInput.value.split(/[,，\s]+/).map((item) => item.trim().replace(/^#/, "")).filter(Boolean);
}
function selectItem(row: GoodStuffItem) {
  selectedItem.value = row;
}
async function copyItem(row: GoodStuffItem) {
  await copyJson(row, "素材已复制");
}
async function copyCurrentView() {
  await copyJson({
    filters: { ...filters, quality: qualityFilter.value },
    metrics: qualityMetrics.value,
    items: filteredItems.value
  }, "当前素材视图已复制");
}
async function load(announce = true) {
  loading.value = true;
  error.value = "";
  if (announce) actionResult.value = "";
  try {
    const params = new URLSearchParams();
    if (filters.item_type) params.set("item_type", filters.item_type);
    if (filters.status) params.set("status", filters.status);
    if (filters.q) params.set("q", filters.q);
    if (filters.tag) params.set("tag", filters.tag.replace(/^#/, ""));
    params.set("limit", "200");
    const payload = await api<{ items: GoodStuffItem[] }>(`/api/good-stuff?${params.toString()}`);
    items.value = payload.items || [];
    if (selectedItem.value && !items.value.some((item) => item.id === selectedItem.value?.id)) selectedItem.value = null;
    else if (!selectedItem.value) selectedItem.value = items.value[0] || null;
    else selectedItem.value = items.value.find((item) => item.id === selectedItem.value?.id) || selectedItem.value;
    if (announce) actionResult.value = `已加载 ${items.value.length} 条好东西`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载好东西失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
function edit(row: GoodStuffItem) {
  selectedItem.value = row;
  form.id = row.id;
  form.item_type = row.item_type;
  form.title = row.title;
  form.content = row.content;
  form.url = row.url;
  form.score = row.score;
  tagInput.value = (row.tags || []).join(", ");
}
function resetForm() {
  form.id = 0;
  form.item_type = "note";
  form.title = "";
  form.content = "";
  form.url = "";
  form.score = 0;
  tagInput.value = "";
}
async function save() {
  const payload = { ...form, tags: tagsFromInput() };
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    if (form.id) {
      await putJson(`/api/good-stuff/${form.id}`, payload);
      actionResult.value = "素材已保存";
      toast.push("已保存");
    } else {
      await postJson("/api/good-stuff", payload);
      actionResult.value = "素材已添加";
      toast.push("已添加");
    }
    resetForm();
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "保存好东西失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function archive(row: GoodStuffItem) {
  if (!window.confirm("确定归档这条好东西？")) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await api(`/api/good-stuff/${row.id}`, { method: "DELETE" });
    actionResult.value = "素材已归档";
    toast.push("已归档");
    if (randomItem.value?.id === row.id) randomItem.value = null;
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "归档失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function restore(row: GoodStuffItem) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await putJson(`/api/good-stuff/${row.id}`, { status: "active" });
    actionResult.value = "素材已恢复";
    toast.push("已恢复");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "恢复失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function randomPick() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const suffix = filters.item_type ? `?item_type=${encodeURIComponent(filters.item_type)}` : "";
    const payload = await api<{ item: GoodStuffItem }>(`/api/good-stuff/random${suffix}`);
    randomItem.value = payload.item;
    selectedItem.value = payload.item;
    actionResult.value = "随机抽取完成";
  } catch (err) {
    error.value = err instanceof Error ? err.message : "没有可抽取的好东西";
    randomItem.value = null;
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
onMounted(load);
</script>

<style scoped>
.random-pick {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  display: grid;
  gap: 8px;
  margin-bottom: 14px;
  padding: 12px;
}
</style>
